import React from 'react';

type Project = {
  title: string;
  org: string;
  period: string;
  summary: string;
  impact: string;
  tech: string[];
};

const projects: Project[] = [
  {
    title: 'Broadcast data platform for live golf',
    org: 'PGA TOUR',
    period: '2023 – Present',
    summary:
      'Architected and delivered a distributed platform that transforms raw shot-tracking feeds into broadcast-ready data for live golf coverage.',
    impact:
      'Serves real-time data to NBC, CBS, and PGA TOUR Studios with sub-second latency and zero tolerated downtime during tournaments.',
    tech: ['.NET 8/10', 'AWS ECS & Lambda', 'SQS', 'DynamoDB', 'WebSockets', 'React', 'TypeScript'],
  },
  {
    title: 'Automated machine learning platform',
    org: 'Gemini Sports Analytics',
    period: '2022',
    summary:
      'Co-designed and implemented the backend for a SaaS platform that automates ML workflows for sports organizations.',
    impact:
      'Enabled non-technical users to run repeatable modeling pipelines on cloud infrastructure, reducing manual analyst effort and cycle times.',
    tech: ['.NET 6', 'PostgreSQL', 'AWS Lambda', 'S3', 'Step Functions', 'EventBridge'],
  },
  {
    title: 'Baseball ops tooling & data infrastructure',
    org: 'Houston Astros',
    period: '2019 – 2021',
    summary:
      'Built internal web apps and ingestion pipelines powering Baseball Analytics and Operations decision-making.',
    impact:
      'Improved reliability and accessibility of critical baseball data for analysts, coaches, and front office stakeholders.',
    tech: ['C# .NET Core', 'React', 'SQL Server', 'Azure Cosmos DB', 'ETL pipelines'],
  },
];

const Projects: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-300">
        Case studies
        <span className="h-1 w-10 rounded-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-indigo-400" />
      </div>
      <p className="max-w-2xl text-sm text-slate-300">
        A few representative projects that capture how I think about architecture, reliability, and building systems that
        unlock real business and performance outcomes.
      </p>
      <div className="grid gap-5 md:grid-cols-3">
        {projects.map((project) => (
          <article
            key={project.title}
            className="flex flex-col rounded-2xl border border-slate-800/80 bg-slate-900/60 p-4 text-xs text-slate-200 shadow-sm"
          >
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-slate-50">{project.title}</h3>
              <p className="text-[11px] text-slate-400">
                {project.org} · {project.period}
              </p>
            </div>
            <p className="mb-2 leading-relaxed text-slate-200">{project.summary}</p>
            <p className="mb-3 text-[11px] text-emerald-300">{project.impact}</p>
            <div className="mt-auto flex flex-wrap gap-1">
              {project.tech.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-slate-700/80 bg-slate-900/70 px-2 py-0.5 text-[10px] text-slate-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default Projects;

